import React, {useCallback, useEffect, useState} from 'react';
import {Button, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AgyleAccordion from '../../../components/ui/accordion/agyleAccordion';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import Formatter from '../../../util/formatter';
import {user} from '../../../store/reducers/user';
import * as _ from 'lodash';
import Divider from '@mui/material/Divider';
import {styled} from '@mui/styles';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import {setSnackbar} from '../../../store/reducers/globals';
import {ControlClinician} from '../../../controls/agyle/clinitian';
import API from '../../../app/api';
import APIForm from '../../../app/apiForm';
import Notes from '../../../util/notes';
import MessageBox from '../../../util/messageBox';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import useOpenState from '../../../hooks/useOpenState';
import {useTranslation} from 'react-i18next';
import InformationBox from '../../../components/other/informationBox';
import AgyleCheckBox from '../../../components/ui/checkbox/agyleCheckBox';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    divider: {
        width: '100%',
        // alignItems: 'center',
        margin: '5px',
    },
    blockSwitch: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    textField: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    provideDetailsLabel: {
        alignItems: 'center',
    },
    radio: {
        width: '100%',
        minWidth: '100%',
    },
    proceedExamination: {
        color: '#c70000',
        fontWeight: 'bold',
    },
    columnRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    error: {
        color: theme.palette.error.main,
    },
}));

const REASON_EXAMPLE = [
    '1. The death was due to poisoning including by on otherwise benign substance',
    '2. The death was due to exposure to, or contact with a toxic substance',
    '3. The death was due to the use of a medicinal product, the use of a controlled drug or psychoactive substance',
    '4. The death was due to violence, trauma or injury',
    '5. The death was due to self-harm',
    '6. The death was due to neglect, including self-neglect',
    '7. The death was due to a person undergoing any treatment or procedure of a medical or similar nature',
    '8. The death was due to an injury or disease attributable to any employment held by the person during the person’s lifetime',
    '9. The person’s death was unnatural but does not fall within any of the above circumstances',
    '10. The cause of death is unknown',
    '11. The registered medical practitioner suspects that the person died while in custody or otherwise in state detention',
    '12. There was no attending registered medical practitioner, and there is no other registered medical practitioner to sign a medical certificate cause of death in relation to the deceased person',
    '13. The attending medical practitioner is not available within a reasonable time of the person’s death to sign the certificate of cause of death',
    '14. The identity of the deceased person is unknown',
];

const CustomWidthTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} classes={{popper: className}}/>
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 800,
    },
});

const VerificationOfDeath = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // User object
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);
    const data = props.patient?.studies?.verificationOfDeath || null;

    // Dicts
    const [roles, setRoles] = useState([]);
    const [wards, setWards] = useState([]);

    const initVerificationOfDeath = {
        // clinicianName: userObject || null,
        clinicianName: null,
        clinicianGrade: null,
        clinicianLocation: null,
        iLooked: null,
        methodOfIdentification: {
            wristband: null,
        },
        thosePresent: '',

        absenceExamine: {
            centralPulse: false,
            hearthBreathSounds: false,
            pupilatoryResponses: false,
            cornealReflexes: false,
            motorResponse: false,
        },

        dateOfDeath: moment(),
        timeOfDeath: null,

        declaration: '',

        coronerRef: '',
        causeOfDeath1: '',
        causeOfDeath2: '',
        causeOfDeath3: '',
        causeOfDeath4: '',

        circumstances: '',

        clinicianEmail: '',
        when: moment().toISOString(),
        by: tester,
    };

    const [verificationOfDeath, setVerificationOfDeath] = useState(initVerificationOfDeath);
    const [accordionExpandedState, setAccordionExpandedState] = useState({
        verification: true,
        cause: true,
    });

    // Opener
    const [isClearConfirmOpen, openClearConfirm, closeClearConfirm] = useOpenState(false, true);

    // Others
    const exceptionFields = ['causeOfDeath2', 'causeOfDeath3', 'causeOfDeath4', 'thosePresent'];

    useEffect(() => {
        if (data) {
            setVerificationOfDeath({...initVerificationOfDeath, ...data});
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        APIForm.getDictForm('roles.all', '{"forVoD": true}')
            .then((res) => {
                if (res) setRoles(res);
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot to load roles.'}));
            });

        API.getDictByURL(`/$dict/Clinicians`)
            .then((dictData) => {
                if (!isMounted) return;

                if (dictData) {
                    for (let clinician of dictData.__data) {
                        if (clinician.userName === userObject.userName) {
                            setVerificationOfDeath(pr => ({
                                ...pr,
                                clinicianName: clinician,
                            }));
                            break;
                        }
                    }
                }
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot to load Clinicians.'}));
            });

        API.getDictByURL(`/$dict/Nurses`)
            .then((dictData) => {
                if (!isMounted) return;

                if (dictData) {
                    for (let clinician of dictData.__data) {
                        if (clinician.userName === userObject.userName) {
                            setVerificationOfDeath(pr => ({
                                ...pr,
                                clinicianName: clinician,
                            }));
                            break;
                        }
                    }
                }
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot to load Nurses.'}));
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        API.getAllWards(controller.signal)
            .then(response => {
                if (response?.data) {
                    const ed = {
                        wardCode: 'ED',
                        wardName: 'EMERGENCY DEPARTMENT',
                        hospital: null,
                        __id: 'ED',
                    };

                    const wards = [...response?.data];
                    // If ward list doesn't have ED than insert
                    if (_.findIndex(wards, {wardCode: 'ED'}) === -1) {
                        wards.push(ed);
                    }

                    setWards(_.orderBy(wards, ['wardName'], ['asc']));
                    setVerificationOfDeath(pr => ({
                        ...pr,
                        clinicianLocation: ed,
                    }));
                }
            })
            .catch(err => {
                console.error(err);
                dispatch(setSnackbar({type: 'error', message: 'Cannot to load list of wards.'}));
            });

        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        if (props.onCustomButtons) {
            props.onCustomButtons(createButton());
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSaveForm);
        }
        if (props.onDisableSubmit) {
            props.onDisableSubmit(isThereAnyNull() || props.disableSave);
        }
    }, [verificationOfDeath, props.disableSave]);

    const handleChangeRadio = (event) => {
        const {name, value} = event.target;
        setVerificationOfDeath(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeRadioInner = (event, innerField) => {
        const {name, value} = event.target;
        const boolVal = value?.toLowerCase() === 'true';

        setVerificationOfDeath(pr => ({
            ...pr,
            [innerField]: {
                ...pr[innerField],
                [name]: boolVal,
            },
        }));
    };

    const handleChangeTextField = (event) => {
        const {name, value} = event.target;

        setVerificationOfDeath(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeCheckbox = (event, objectKey) => {
        const {name, checked} = event.target;
        setVerificationOfDeath((pr) =>
            ({
                ...pr,
                [objectKey]: {
                    ...pr[objectKey],
                    [name]: checked,
                },
            }));
    };

    const handleChangeAccordion = (param) => {
        setAccordionExpandedState((pr) =>
            ({
                ...pr,
                [param]: !pr[param],
            }));
    };

    const handleData = (event, value) => {
        setVerificationOfDeath(pr => ({
            ...pr,
            clinicianName: event,
        }));
    };

    const handleChangeDateOfDeath = (param) => {
        setVerificationOfDeath((pr) =>
            ({
                ...pr,
                dateOfDeath: param,
            }));
    };

    const handleChangeTimeOfDeath = (param) => {
        setVerificationOfDeath((pr) =>
            ({
                ...pr,
                timeOfDeath: param,
            }));
    };

    const isThereAnyNull = () => {
        for (const key in verificationOfDeath) {
            if (
                verificationOfDeath.hasOwnProperty(key) &&
                !(key === 'causeOfDeath1' && verificationOfDeath['coronerRef'] === 'suspect') &&
                !exceptionFields.includes(key) &&
                (verificationOfDeath[key] === '' || verificationOfDeath[key] === null)
            ) {
                return true;
            }
        }

        for (const key in verificationOfDeath['absenceExamine']) {
            if (
                verificationOfDeath['absenceExamine'].hasOwnProperty(key) && !verificationOfDeath['absenceExamine'][key]
            ) {
                return true;
            }
        }

        for (const key in verificationOfDeath['methodOfIdentification']) {
            if (
                verificationOfDeath['methodOfIdentification'].hasOwnProperty(key) && !verificationOfDeath['methodOfIdentification'][key]
            ) {
                return true;
            }
        }
        return false;
    };

    const handleSaveForm = () => {
        const objectToSave = {
            ...verificationOfDeath,
            lastUpdateBy: tester,
            lastUpdateDate: moment().toISOString(),
        };

        let dataArray = [{
            name: 'studies.verificationOfDeath',
            value: objectToSave,

            noteType: 'studies.verificationOfDeath',
            noteToAdd: Notes.getVerificationOfDeathNote(objectToSave),
        },
            {
                'metadata.dischargeTimestampData': verificationOfDeath,
            }];

        props.onChange(dataArray);
    };

    const clearData = () => {
        const objectToSave = {
            lastUpdateBy: tester,
            lastUpdateDate: moment().toISOString(),
        };
        let dataArray = [{
            name: 'studies.verificationOfDeath',
            value: objectToSave,

            noteType: 'studies.verificationOfDeath',
            noteToAdd: Notes.getVerificationOfDeathNote(objectToSave),
        }];

        closeClearConfirm();
        props._customOnSave(dataArray);
    };

    const createButton = () => ([
        <AgyleButton
            key="pdf-button"
            onClick={() => {
                console.log(verificationOfDeath);
                props.getDoR('verificationOfDeath');
            }}
        >
            {t('PDF')}
        </AgyleButton>,

        <AgyleButton
            key="delete-button"
            onClick={openClearConfirm}
        >
            {t('Delete')}
        </AgyleButton>,
    ]);

    const exampleReason = useCallback(() => {
        return (
            <div>
                {REASON_EXAMPLE.map((text, index) => (
                    <div className={classes.label} key={index}>
                        {t(text)}
                    </div>
                ))}
            </div>
        );
    }, []);

    return (
        <>
            <MessageBox
                title={t('Caution')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={clearData}
                onClose={closeClearConfirm}
                message={t('Are you sure you want delete all recorded values?')}
                open={isClearConfirmOpen}
            />

            <AgyleAccordion
                className={classes.gridContainer}
                expanded={accordionExpandedState.verification}
                onChange={() => handleChangeAccordion('verification')}
                label={t('Verification of Death')}
                formBackground
            >
                <Grid container spacing={2} alignItems={'center'}>
                    <Grid item xs={6}>
                        <FormLabel>
                            {t('Name of person verifying death')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <ControlClinician
                            agyleRefresh
                            size="small"
                            error={_.isNull(verificationOfDeath.clinicianName)}
                            label={t('Clinician')}
                            value={verificationOfDeath.clinicianName}
                            onChange={handleData}
                            cliniciansWithNurses
                            disabled
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormLabel>
                            {t('Grade of person verifying death')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleDictionaryField
                            label={t('Grade')}
                            getOptionLabel={option => option.name}
                            options={roles}
                            value={verificationOfDeath.clinicianGrade}
                            onChange={(event, newValue) => setVerificationOfDeath(pr => ({
                                ...pr,
                                clinicianGrade: newValue,
                            }))}
                            error={_.isNull(verificationOfDeath.clinicianGrade)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormLabel>
                            {t('Location of patient')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleDictionaryField
                            label={t('Ward')}
                            onChange={(_e, val) =>
                                setVerificationOfDeath((pr) => ({
                                    ...pr,
                                    clinicianLocation: val,
                                }))
                            }
                            options={wards}
                            value={verificationOfDeath.clinicianLocation}
                            getOptionLabel={option => option.wardName}
                            error={_.isNull(verificationOfDeath.wardName)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormLabel className={classes.label}>
                            {t('I looked after the patient while alive')}:
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <FormControl>
                            <RadioGroup
                                row
                                name="iLooked"
                                onChange={handleChangeRadio}
                                value={verificationOfDeath.iLooked}
                            >
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio
                                        className={clsx(_.isNull(verificationOfDeath.iLooked) && classes.error)}/>}
                                    label={t('Yes')}
                                />
                                <FormControlLabel
                                    value="no"
                                    control={<Radio
                                        className={clsx(_.isNull(verificationOfDeath.iLooked) && classes.error)}/>}
                                    label={t('No')}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                        <FormLabel className={classes.label}>
                            {t('I checked the patient\'s wristband')}:
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'methodOfIdentification')}
                            value={verificationOfDeath.methodOfIdentification.wristband}
                            name="wristband"
                            error={_.isNull(verificationOfDeath?.methodOfIdentification?.wristband)}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <FormLabel>
                            {t('Those present')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleTextField
                            fullWidth
                            name="thosePresent"
                            label={t('Provide details...')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.thosePresent}
                            variant="outlined"
                            error={_.isNull(verificationOfDeath.thosePresent)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Divider className={classes.divider}/>

                    <Grid item xs={12}>
                        <FormLabel>
                            {t('Examination')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={12} className={classes.columnRow}>
                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'absenceExamine')}
                            value={verificationOfDeath.absenceExamine.centralPulse}
                            name="centralPulse"
                            label={t('Absence of a central pulse on palpitation for five minutes')}
                            error={!verificationOfDeath.absenceExamine.centralPulse}
                        />
                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'absenceExamine')}
                            value={verificationOfDeath.absenceExamine.hearthBreathSounds}
                            name="hearthBreathSounds"
                            label={t('Absence of heart and breath sounds on auscultation for five minutes')}
                            error={!verificationOfDeath.absenceExamine.hearthBreathSounds}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <div className={classes.proceedExamination}>
                            {t('Do not proceed to neurological examination until the five-minute examination has been completed')}
                        </div>
                    </Grid>

                    <Grid item xs={12} className={classes.columnRow}>
                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'absenceExamine')}
                            value={verificationOfDeath.absenceExamine.pupilatoryResponses}
                            name="pupilatoryResponses"
                            label={t('Absence of the pupillary responses to light')}
                            error={!verificationOfDeath.absenceExamine.pupilatoryResponses}
                        />

                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'absenceExamine')}
                            value={verificationOfDeath.absenceExamine.cornealReflexes}
                            name="cornealReflexes"
                            label={t('Absence of the corneal reflexes')}
                            error={!verificationOfDeath.absenceExamine.cornealReflexes}
                        />

                        <AgyleCheckBox
                            onChange={(event) => handleChangeCheckbox(event, 'absenceExamine')}
                            value={verificationOfDeath.absenceExamine.motorResponse}
                            name="motorResponse"
                            label={t('Absence of any motor response to supra-orbital pressure')}
                            error={!verificationOfDeath.absenceExamine.motorResponse}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleDatePicker
                            label={t('Date of death')}
                            value={verificationOfDeath.dateOfDeath}
                            onChange={handleChangeDateOfDeath}
                            type="date"
                            disablePast={false}
                            error={_.isNull(verificationOfDeath.dateOfDeath)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <AgyleDatePicker
                            label={t('Time of death')}
                            value={verificationOfDeath.timeOfDeath}
                            onChange={handleChangeTimeOfDeath}
                            type="time"
                            disablePast={false}
                            error={_.isNull(verificationOfDeath.timeOfDeath)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Divider className={classes.divider}/>

                    <Grid item xs={12}>
                        <FormControl>
                            <FormLabel className={classes.label}>
                                {t('Declaration')}:
                            </FormLabel>

                            <RadioGroup
                                row
                                name="declaration"
                                onChange={handleChangeRadio}
                                value={verificationOfDeath.declaration}
                            >
                                <FormControlLabel
                                    value="noDec"
                                    control={<Radio
                                        className={clsx(_.isEmpty(verificationOfDeath.declaration) && classes.error)}/>}
                                    label={t('No declaration made')}
                                />

                                <FormControlLabel
                                    value="dec"
                                    control={<Radio
                                        className={clsx(_.isEmpty(verificationOfDeath.declaration) && classes.error)}/>}
                                    label={t('I declare that I have confirmed the identity of this patient and that death has occurred at the date and time given above')}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                </Grid>
            </AgyleAccordion>

            <AgyleAccordion
                className={classes.gridContainer}
                expanded={accordionExpandedState.cause}
                onChange={() => handleChangeAccordion('cause')}
                label={t('Cause of Death')}
                formBackground
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <FormControl>
                            <FormLabel className={classes.label}>
                                {t('Coroner Referral - Medical Cause of Death')}
                            </FormLabel>

                            <RadioGroup
                                row
                                name="coronerRef"
                                onChange={handleChangeRadio}
                                value={verificationOfDeath.coronerRef}
                            >
                                <FormControlLabel
                                    value="suspect"
                                    control={<Radio
                                        className={clsx(_.isEmpty(verificationOfDeath.coronerRef) && classes.error)}/>}
                                    label={t('I have reason to suspect that this person’s death requires the Senior Coroner for Dorset to be notified')}
                                />

                                <FormControlLabel
                                    value="confirmed"
                                    control={<Radio
                                        className={clsx(_.isEmpty(verificationOfDeath.coronerRef) && classes.error)}/>}
                                    label={t('I do not have reason to suspect that this person\'s death requires the Senior Coroner for Dorset to be noticed, and I feel the following is the medical cause of death')}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid container item xs={12} justifyContent="center">
                        <CustomWidthTooltip title={exampleReason()}>
                            <Button>
                                {t('Example Reasons for Coroners Referral')}
                            </Button>
                        </CustomWidthTooltip>
                    </Grid>

                    <Grid item xs={12} style={{paddingLeft: '70px'}}>
                        <FormLabel className={classes.label}>
                            {t('Proposed cause of death, pending Medical Examiner scrutiny:')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={0.5}>
                        <FormLabel className={classes.textField}>
                            {t('1a')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={11.5}>
                        <AgyleTextField
                            fullWidth
                            disabled={verificationOfDeath.coronerRef !== 'confirmed'}
                            name="causeOfDeath1"
                            label={t('Disease or Condition leading directly to death')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.causeOfDeath1}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.circumstances) && verificationOfDeath.coronerRef === 'confirmed'}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={0.5}>
                        <FormLabel className={classes.textField}>
                            {t('1b')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={11.5}>
                        <AgyleTextField
                            fullWidth
                            disabled={verificationOfDeath.coronerRef !== 'confirmed'}
                            name="causeOfDeath2"
                            label={t('Other diseases or conditions If any, leading to 1a')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.causeOfDeath2}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.circumstances) && verificationOfDeath.coronerRef === 'confirmed'}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={0.5}>
                        <FormLabel className={classes.textField}>
                            {t('2a')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={11.5}>
                        <AgyleTextField
                            fullWidth
                            disabled={verificationOfDeath.coronerRef !== 'confirmed'}
                            name="causeOfDeath3"
                            label={t('Other diseases or conditions If any, leading to 1b')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.causeOfDeath3}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.circumstances) && verificationOfDeath.coronerRef === 'confirmed'}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={0.5}>
                        <FormLabel className={classes.textField}>
                            {t('2b')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={11.5}>
                        <AgyleTextField
                            fullWidth
                            disabled={verificationOfDeath.coronerRef !== 'confirmed'}
                            name="causeOfDeath4"
                            label={t('Other significant conditions contributing to death')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.causeOfDeath4}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.circumstances) && verificationOfDeath.coronerRef === 'confirmed'}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel className={classes.label}>
                            {t('(In all cases) Provide a short narrative to explain the circumstances surrounding death, and (If necessary) why you feel the Coroner should be informed')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            fullWidth
                            name="circumstances"
                            label={t('Provide details')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.circumstances}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.circumstances)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormLabel className={classes.label}>
                            {t('Provide your preferred email address in case the Medical Examiner or Coroner’s Officer needs to contact you')}
                        </FormLabel>
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleTextField
                            fullWidth
                            name="clinicianEmail"
                            label={t('Provide details')}
                            className={classes.input}
                            maxRows={25}
                            debounce
                            onChange={handleChangeTextField}
                            value={verificationOfDeath.clinicianEmail}
                            variant="outlined"
                            error={_.isEmpty(verificationOfDeath.clinicianEmail)}
                            noBorder={false}
                            withNewBackground
                        />
                    </Grid>
                </Grid>
            </AgyleAccordion>

            <InformationBox type="warning-white">
                <div>
                    {t('Once submitted, completed forms will be automatically emailed upon discharge to MedicalExaminers@dchft.nhs.uk')}
                </div>
                <div>
                    {t('For telephone advice Monday-Friday 0800-1600 (excluding Bank Holidays) call')}
                </div>
                <div>
                    {t('01305 255251 Out of Hours, contact the on-call Mortuary Tech via switchboard who will assist')}
                </div>
                <br/>
                <div>
                    {t('**all users will still need to mark the patient as deceased in PAS**')}
                </div>
            </InformationBox>
        </>
    );
};
export default VerificationOfDeath;